import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Divider,
    Chip,
} from '@mui/material';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { toast } from 'react-toastify';
import { check_enhance_coupon, submit_enhancement, get_enhancement_list, get_settings } from '../../../api/ControllApi';
import { useUser } from '../../../context/LoginDetail';

const BANK_VALIDATORS = {
    bank_name: {
        label: 'Bank Name',
        validate: (v) => v.trim().length >= 2,
        message: 'Bank name must be at least 2 characters.',
    },
    bank_account_number: {
        label: 'Account Number',
        validate: (v) => /^\d{9,18}$/.test(v.trim()),
        message: 'Account number must be 9–18 digits.',
    },
    bank_ifsc_code: {
        label: 'IFSC Code',
        validate: (v) => /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v.trim().toUpperCase()),
        message: 'Enter a valid IFSC code (e.g. HDFC0001234).',
    },
    bank_beneficiary_name: {
        label: 'Beneficiary Name',
        validate: (v) => /^[a-zA-Z\s]{2,}$/.test(v.trim()),
        message: 'Beneficiary name must contain only letters (min 2 chars).',
    },
    bank_phone_number: {
        label: 'Phone Number',
        validate: (v) => /^[6-9]\d{9}$/.test(v.trim()),
        message: 'Enter a valid 10-digit Indian mobile number.',
    },
};

const UPI_VALIDATORS = {
    gpay_upi_id: {
        label: 'UPI ID',
        validate: (v) => /^[\w.\-]{2,}@[\w]{2,}$/.test(v.trim()),
        message: 'Enter a valid UPI ID (e.g. name@upi).',
    },
    gpay_phone_number: {
        label: 'Phone Number',
        validate: (v) => /^[6-9]\d{9}$/.test(v.trim()),
        message: 'Enter a valid 10-digit Indian mobile number.',
    },
};

const INITIAL_FORM = {
    bank_name: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    bank_beneficiary_name: '',
    bank_phone_number: '',
    gpay_upi_id: '',
    gpay_phone_number: '',
};

function validate(paymentType, formData) {
    const validators = paymentType === 'bank' ? BANK_VALIDATORS : UPI_VALIDATORS;
    const errors = {};
    for (const [field, rule] of Object.entries(validators)) {
        const value = formData[field] || '';
        if (!value.trim()) {
            errors[field] = `${rule.label} is required.`;
        } else if (!rule.validate(value)) {
            errors[field] = rule.message;
        }
    }
    return errors;
}

const FieldError = ({ msg }) =>
    msg ? <Typography sx={{ color: '#D32F2F', fontSize: '11px', mt: 0.5, pl: 1 }}>{msg}</Typography> : null;

function Enchashment() {
    const [couponCode, setCouponCode] = useState('');
    const [isValidated, setIsValidated] = useState(false);
    const [couponDetails, setCouponDetails] = useState(null);
    const [paymentType, setPaymentType] = useState('bank');
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [encashmentList, setEncashmentList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [encashmentNote, setEncashmentNote] = useState('');
    const { user } = useUser();

    useEffect(() => {
        if (user?.id) fetchEncashmentList(user.id);
        get_settings()
            .then(res => {
                if (res?.data?.encashment_description) setEncashmentNote(res.data.encashment_description);
            })
            .catch(err => { });
    }, [user?.id]);

    const fetchEncashmentList = async (userId) => {
        if (!userId) return;
        setListLoading(true);
        try {
            const res = await get_enhancement_list(userId);
            if (res.result || res.status) setEncashmentList(res.data || []);
        } catch (err) {
        } finally {
            setListLoading(false);
        }
    };

    const handleValidate = async () => {
        if (!couponCode.trim()) { toast.error("Please enter a coupon code"); return; }
        setLoading(true);
        try {
            const res = await check_enhance_coupon(couponCode.trim());
            if (res.result || res.status) {
                setIsValidated(true);
                setCouponDetails(res.data);
                toast.success(res.message || "Coupon validated successfully");
            } else {
                toast.error(res.message || "Invalid coupon code");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error validating coupon");
        } finally {
            setLoading(false);
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        const errors = validate(paymentType, { ...formData, [name]: value });
        setFieldErrors(prev => ({ ...prev, [name]: errors[name] }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            const errors = validate(paymentType, { ...formData, [name]: value });
            setFieldErrors(prev => ({ ...prev, [name]: errors[name] }));
        }
    };

    const handlePaymentTypeChange = (type) => {
        setPaymentType(type);
        setFieldErrors({});
        setTouched({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validators = paymentType === 'bank' ? BANK_VALIDATORS : UPI_VALIDATORS;
        const allTouched = Object.keys(validators).reduce((acc, k) => ({ ...acc, [k]: true }), {});
        setTouched(allTouched);

        const errors = validate(paymentType, formData);
        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            toast.error("Please fix highlighted errors");
            return;
        }

        setLoading(true);

        const payload = {
            code: couponCode.trim(),
            payment_type: paymentType,
            amount: couponDetails?.total_amount || 0,
            tax: couponDetails?.coupon_discount || 0,
            total: couponDetails?.total_after_discount || 0,
            ...formData,
        };

        if (paymentType === 'bank') {
            delete payload.gpay_upi_id;
            delete payload.gpay_phone_number;
        } else {
            delete payload.bank_name;
            delete payload.bank_account_number;
            delete payload.bank_ifsc_code;
            delete payload.bank_beneficiary_name;
            delete payload.bank_phone_number;
        }

        try {
            const res = await submit_enhancement(payload);
            if (res.status || res.result) {
                toast.success(res.message || "Enhancement submitted successfully");
                setIsValidated(false);
                setCouponCode('');
                setFormData(INITIAL_FORM);
                setFieldErrors({});
                setTouched({});
                fetchEncashmentList(user?.id);
            } else {
                toast.error(res.message || "Submission failed");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error submitting enhancement");
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = (name) => ({
        width: '100%',
        height: '44px',
        borderRadius: '20px',
        border: touched[name] && fieldErrors[name] ? '1px solid #D32F2F' : '1px solid #D5E2CE',
        padding: '0 16px',
        fontSize: '13px',
        outline: 'none',
        backgroundColor: '#ffffff',
    });

    return (
        <Box
            sx={{
                maxWidth: 560,
                mx: 'auto',
                bgcolor: '#ffffff',
                borderRadius: '24px',
                border: '1px solid #E6EFE3',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
                p: { xs: 3, md: 4 },
                my: 2,
            }}
        >
            {/* Title */}
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 700,
                    color: '#3B6019',
                    textAlign: 'center',
                    fontSize: '20px',
                    mb: 3,
                }}
            >
                Encashment
            </Typography>

            {/* Coupons Code Section Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ConfirmationNumberOutlinedIcon sx={{ color: '#3B6019', fontSize: 20 }} />
                <Typography sx={{ fontWeight: 700, color: '#3B6019', fontSize: '15px' }}>
                    Coupons Code
                </Typography>
            </Box>

            {!isValidated ? (
                /* Input & Validate Row */
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #D5E2CE',
                            borderRadius: '24px',
                            height: 48,
                            px: 2.5,
                            bgcolor: '#ffffff',
                            '&:focus-within': {
                                borderColor: '#5B9E00',
                                boxShadow: '0 0 0 2px rgba(91,158,0,0.15)',
                            },
                        }}
                    >
                        <input
                            type="text"
                            placeholder="Enter Coupon Code"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            style={{
                                border: 'none',
                                outline: 'none',
                                width: '100%',
                                fontSize: '14px',
                                color: '#333333',
                                background: 'transparent',
                            }}
                        />
                    </Box>
                    <Button
                        onClick={handleValidate}
                        disabled={loading}
                        sx={{
                            bgcolor: '#2E5B10',
                            color: '#ffffff',
                            borderRadius: '24px',
                            px: 3.5,
                            height: 48,
                            fontWeight: 700,
                            fontSize: '14px',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#22460B',
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Validate'}
                    </Button>
                </Box>
            ) : (
                /* Form after validation */
                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2 }}>
                    <Box sx={{ bgcolor: '#F5F9F3', border: '1px solid #D5E2CE', borderRadius: '16px', p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontWeight: 700, color: '#3B6019', fontSize: '14px' }}>
                                Coupon Validated
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => setIsValidated(false)}
                                sx={{ textTransform: 'none', color: '#888888', fontSize: '12px' }}
                            >
                                Change
                            </Button>
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#444' }}>
                            Amount: <strong>₹{couponDetails?.total_amount}</strong> | Net: <strong style={{ color: '#2E5B10' }}>₹{couponDetails?.total_after_discount}</strong>
                        </Typography>
                    </Box>

                    {/* Method Tabs */}
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                        <Button
                            onClick={() => handlePaymentTypeChange('bank')}
                            sx={{
                                flex: 1,
                                height: 40,
                                borderRadius: '20px',
                                fontSize: '13px',
                                fontWeight: 600,
                                textTransform: 'none',
                                bgcolor: paymentType === 'bank' ? '#5B9E00' : '#F0F4EE',
                                color: paymentType === 'bank' ? '#fff' : '#444',
                                '&:hover': { bgcolor: paymentType === 'bank' ? '#4e8a00' : '#e0e8dc' },
                            }}
                        >
                            Bank Account
                        </Button>
                        <Button
                            onClick={() => handlePaymentTypeChange('upi')}
                            sx={{
                                flex: 1,
                                height: 40,
                                borderRadius: '20px',
                                fontSize: '13px',
                                fontWeight: 600,
                                textTransform: 'none',
                                bgcolor: paymentType === 'upi' ? '#5B9E00' : '#F0F4EE',
                                color: paymentType === 'upi' ? '#fff' : '#444',
                                '&:hover': { bgcolor: paymentType === 'upi' ? '#4e8a00' : '#e0e8dc' },
                            }}
                        >
                            UPI Payment
                        </Button>
                    </Box>

                    {paymentType === 'bank' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                            <div>
                                <input placeholder="Bank Name" name="bank_name" value={formData.bank_name} onChange={handleInputChange} onBlur={handleBlur} style={inputStyle('bank_name')} />
                                <FieldError msg={fieldErrors.bank_name} />
                            </div>
                            <div>
                                <input placeholder="Account Number" name="bank_account_number" value={formData.bank_account_number} onChange={handleInputChange} onBlur={handleBlur} style={inputStyle('bank_account_number')} />
                                <FieldError msg={fieldErrors.bank_account_number} />
                            </div>
                            <div>
                                <input placeholder="IFSC Code" name="bank_ifsc_code" value={formData.bank_ifsc_code} onChange={handleInputChange} onBlur={handleBlur} style={inputStyle('bank_ifsc_code')} />
                                <FieldError msg={fieldErrors.bank_ifsc_code} />
                            </div>
                            <div>
                                <input placeholder="Beneficiary Name" name="bank_beneficiary_name" value={formData.bank_beneficiary_name} onChange={handleInputChange} onBlur={handleBlur} style={inputStyle('bank_beneficiary_name')} />
                                <FieldError msg={fieldErrors.bank_beneficiary_name} />
                            </div>
                            <div>
                                <input placeholder="Phone Number" name="bank_phone_number" value={formData.bank_phone_number} onChange={handleInputChange} onBlur={handleBlur} style={inputStyle('bank_phone_number')} />
                                <FieldError msg={fieldErrors.bank_phone_number} />
                            </div>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                            <div>
                                <input placeholder="UPI ID (e.g. name@upi)" name="gpay_upi_id" value={formData.gpay_upi_id} onChange={handleInputChange} onBlur={handleBlur} style={inputStyle('gpay_upi_id')} />
                                <FieldError msg={fieldErrors.gpay_upi_id} />
                            </div>
                            <div>
                                <input placeholder="UPI Linked Phone Number" name="gpay_phone_number" value={formData.gpay_phone_number} onChange={handleInputChange} onBlur={handleBlur} style={inputStyle('gpay_phone_number')} />
                                <FieldError msg={fieldErrors.gpay_phone_number} />
                            </div>
                        </Box>
                    )}

                    <Button
                        type="submit"
                        disabled={loading}
                        sx={{
                            width: '100%',
                            height: 48,
                            borderRadius: '24px',
                            bgcolor: '#2E5B10',
                            color: '#ffffff',
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#22460B' },
                        }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit for Encashment'}
                    </Button>
                </Box>
            )}

            {/* Note Alert Box */}
            <Box
                sx={{
                    bgcolor: '#F6FAF5',
                    border: '1px solid #E2EFE0',
                    borderRadius: '16px',
                    p: 2,
                    mt: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#5B9E00' }} />
                    <Typography sx={{ fontWeight: 700, color: '#2E5B10', fontSize: '13px' }}>
                        Note:
                    </Typography>
                </Box>
                <Typography sx={{ color: '#556650', fontSize: '12px', lineHeight: 1.5, pl: 2 }}>
                    Make sure to apply your gift coupon and check availability. A 20% service charge will be deducted, and the remaining amount will be credited to your account. Please provide correct UPI or bank details to avoid any issues.
                </Typography>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: 3, borderColor: '#EEF4EC' }} />

            {/* Encashment History Section */}
            <Typography sx={{ fontWeight: 700, color: '#3B6019', fontSize: '15px', mb: 2 }}>
                Encashment History
            </Typography>

            {/* Header Table Container */}
            <Box
                sx={{
                    border: '1px solid #E5EBE3',
                    borderRadius: '16px',
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    bgcolor: '#ffffff',
                    mb: 2,
                }}
            >
                <Typography sx={{ fontWeight: 700, color: '#333333', fontSize: '14px', flex: 1, textAlign: 'center' }}>
                    Code
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#333333', fontSize: '14px', flex: 1, textAlign: 'center' }}>
                    Type
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#333333', fontSize: '14px', flex: 1, textAlign: 'center' }}>
                    Amount
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#333333', fontSize: '14px', flex: 1, textAlign: 'center' }}>
                    Status
                </Typography>
            </Box>

            {/* Data / Empty State */}
            {listLoading ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: '#3B6019' }} />
                </Box>
            ) : encashmentList.length > 0 ? (
                encashmentList.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            border: '1px solid #E8EFE6',
                            borderRadius: '16px',
                            p: 1.5,
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            bgcolor: '#ffffff',
                            mb: 1,
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, fontSize: '13px', flex: 1, textAlign: 'center' }}>
                            {item.name}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', flex: 1, textAlign: 'center', textTransform: 'capitalize' }}>
                            {item.payment_type}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: '#2E5B10', fontSize: '13px', flex: 1, textAlign: 'center' }}>
                            ₹{item.total_amount}
                        </Typography>
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                            <Chip
                                label={item.status === 1 ? 'Approved' : item.status === 2 ? 'Rejected' : 'Pending'}
                                size="small"
                                sx={{
                                    bgcolor: item.status === 1 ? '#E8F5E9' : item.status === 2 ? '#FFEBEE' : '#FFF8E1',
                                    color: item.status === 1 ? '#2E7D32' : item.status === 2 ? '#C62828' : '#F57F17',
                                    fontWeight: 600,
                                    fontSize: '11px',
                                    height: 24,
                                }}
                            />
                        </Box>
                    </Box>
                ))
            ) : (
                <Typography sx={{ color: '#888888', textAlign: 'center', py: 4, fontSize: '13px' }}>
                    No Encashment history found
                </Typography>
            )}
        </Box>
    );
}

export default Enchashment;